import { getAuthToken } from '../../Api/authToken';
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import { API_URL } from '../../Api/api';
import { FiShield, FiInbox, FiX, FiEye, FiEdit2, FiTrash2 } from 'react-icons/fi';
import './AdminPages.css';

const AdminList = () => {
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [currentRole, setCurrentRole] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    admin_name: '',
    email: '',
    admin_phone: '',
    password: '',
    role: 'sub_admin',
    is_super: 0,
    show_user: false,
    user_chat_access: true
  });
  const [editingId, setEditingId] = useState(null);
  const [viewingAdmin, setViewingAdmin] = useState(null);

  console.log('RENDER AdminList: showForm =', showForm, 'isSuperAdmin =', isSuperAdmin);

  useEffect(() => {
    fetchAdmins();
    checkCurrentUser();
  }, []);

  const checkCurrentUser = async () => {
    try {
      // Try local storage first for speed
      const stored = localStorage.getItem('adminUser');
      if (stored) {
        const u = JSON.parse(stored);
        setIsSuperAdmin(u.is_super === 1);
        setCurrentRole(u.role || '');
      }

      const token = getAuthToken();
      if (!token) return;

      const res = await axios.get(`${API_URL}/admin/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setIsSuperAdmin(res.data.is_super === 1);
      setCurrentRole(res.data.role || '');
      // Sync local storage
      localStorage.setItem('adminUser', JSON.stringify(res.data));
    } catch (error) {
      console.error('Failed to get current user info');
    }
  };

  const fetchAdmins = async () => {
    setLoading(true);
    try {
      const token = getAuthToken();
      const res = await axios.get(`${API_URL}/admin/admins`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setAdmins(res.data);
    } catch (error) {
      toast.error('Failed to fetch admins');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    const missingRequired = !formData.admin_name || !formData.email || !formData.admin_phone || (!editingId && !formData.password);
    if (missingRequired) {
      toast.error('Please fill all required fields');
      return;
    }
    setSubmitting(true);
    try {
      const token = getAuthToken();
      const payload = { ...formData };
      if (editingId && !payload.password) {
        delete payload.password;
      }

      if (editingId) {
        await axios.put(`${API_URL}/admin/admins/${editingId}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin updated successfully');
      } else {
        await axios.post(`${API_URL}/admin/admins`, formData, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Admin created successfully');
      }
      setFormData({ admin_name: '', email: '', admin_phone: '', password: '', role: 'sub_admin', is_super: 0, show_user: false, user_chat_access: true });
      setShowForm(false);
      setEditingId(null);
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to create admin');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEditClick = (admin) => {
    setFormData({
      admin_name: admin.admin_name || admin.name || '',
      email: admin.email || '',
      admin_phone: admin.admin_phone || '',
      password: '', // Keep empty for security, only update if provided in backend (or handle separately)
      role: admin.role || 'sub_admin',
      is_super: admin.is_super || 0,
      show_user: admin.show_user || false,
      user_chat_access: admin.user_chat_access !== false
    });
    setEditingId(admin._id);
    setShowForm(true);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteAdmin = async (id) => {
    if (!window.confirm('Are you sure you want to delete this administrator? This action cannot be undone.')) return;
    try {
      const token = getAuthToken();
      await axios.delete(`${API_URL}/admin/admins/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Admin deleted successfully');
      fetchAdmins();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to delete admin');
    }
  };

  return (
    <div className="admin-page">
      <div className="page-header flex-header">
        <div>
          <h1>Admin List</h1>
          <p>Manage administrators and sub-admins</p>
        </div>
        {isSuperAdmin && (
          <button 
            id="create-admin-btn"
            className={`btn-primary ${showForm ? 'btn-danger' : ''}`} 
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (showForm) {
                setEditingId(null);
                setFormData({ admin_name: '', email: '', admin_phone: '', password: '', role: 'sub_admin', is_super: 0, show_user: false, user_chat_access: true });
              }
              setShowForm(prev => !prev);
            }}
            style={{ minWidth: '160px', cursor: 'pointer', zIndex: 10 }}
          >
            <FiShield /> {showForm ? 'Close Form' : 'Create New Admin'}
          </button>
        )}
      </div>

      {/* Create Admin Form — Super Admin only */}
      {showForm && (
        <div className="admin-card animate-fade-in" style={{ display: 'block', visibility: 'visible', opacity: 1 }}>
          <div className="flex-header" style={{ marginBottom: '1.5rem' }}>
            <h2 style={{ fontSize: '1.125rem', fontWeight: 700 }}>{editingId ? 'Edit Administrator' : 'Add New Administrator'}</h2>
            <button className="btn-icon" onClick={() => { setShowForm(false); setEditingId(null); }}><FiX /></button>
          </div>
          
          <form onSubmit={handleCreateAdmin}>
            <div className="filter-box-grid">
              <div className="form-group">
                <label>Full Name *</label>
                <input type="text" placeholder="Enter admin name" value={formData.admin_name} onChange={e => setFormData({...formData, admin_name: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Email Address *</label>
                <input type="email" placeholder="admin@company.com" value={formData.email} onChange={e => setFormData({...formData, email: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Phone Number *</label>
                <input type="text" placeholder="Contact number" value={formData.admin_phone} onChange={e => setFormData({...formData, admin_phone: e.target.value})} required />
              </div>
              <div className="form-group">
                <label>Password {editingId ? '(Leave blank to keep current)' : '*'}</label>
                <input type="password" placeholder={editingId ? "Update password (optional)" : "Set password"} value={formData.password} onChange={e => setFormData({...formData, password: e.target.value})} required={!editingId} />
              </div>
              <div className="form-group">
                <label>Admin Role</label>
                <select value={formData.role} onChange={e => setFormData({...formData, role: e.target.value})}>
                  <option value="admin">Full Admin</option>
                  <option value="sub_admin">Sub Admin</option>
                </select>
              </div>
              <div className="form-group">
                <label>Super Admin Rights</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <label className="toggle-label" style={{ margin: 0 }}>
                    <input type="checkbox" checked={formData.is_super === 1} onChange={e => setFormData({...formData, is_super: e.target.checked ? 1 : 0})} />
                    <div className="toggle-switch"></div>
                  </label>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{formData.is_super === 1 ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="form-group">
                <label>User Chat</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <label className="toggle-label" style={{ margin: 0 }}>
                    <input
                      type="checkbox"
                      checked={formData.user_chat_access !== false}
                      onChange={e => setFormData({ ...formData, user_chat_access: e.target.checked })}
                    />
                    <div className="toggle-switch"></div>
                  </label>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{formData.user_chat_access !== false ? 'Enabled' : 'Disabled'}</span>
                </div>
              </div>
              <div className="form-group">
                <label>Show in Property Details</label>
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginTop: '0.5rem' }}>
                  <label className="toggle-label" style={{ margin: 0 }}>
                    <input type="checkbox" checked={formData.show_user} onChange={e => setFormData({...formData, show_user: e.target.checked})} />
                    <div className="toggle-switch"></div>
                  </label>
                  <span style={{ fontSize: '0.8125rem', fontWeight: 600 }}>{formData.show_user ? 'Show' : 'Hide'}</span>
                </div>
              </div>
            </div>
            
            <div style={{ marginTop: '1.5rem', display: 'flex', gap: '1rem' }}>
              <button type="submit" className="btn-primary" disabled={submitting} style={{ flex: 1 }}>
                {submitting ? 'Processing...' : editingId ? 'Update Administrator' : 'Register Administrator'}
              </button>
              <button type="button" className="btn-secondary" onClick={() => { setShowForm(false); setEditingId(null); }}>Cancel</button>
            </div>
          </form>
        </div>
      )}

      {/* Admin Table */}
      <div className="admin-card table-wrapper">
        {loading ? (
          <div className="skeleton" style={{ height: '300px', borderRadius: '8px' }}></div>
        ) : admins.length === 0 ? (
          <div className="empty-state">
            <FiInbox size={48} />
            <h3>No Admins Found</h3>
            <p>Create your first admin using the button above.</p>
          </div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-hybrid-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Name</th>
                  <th>Email</th>
                  <th>Phone</th>
                  <th>Role</th>
                  <th>Show to Users</th>
                  <th>User Chat</th>
                  <th>Super Admin</th>
                  <th>Joined</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {admins.map(a => (
                  <tr key={a._id}>
                    <td><span className="hybrid-id">#{a.admin_id || a._id?.substring(a._id.length - 4)}</span></td>
                    <td>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                        <strong>{a.admin_name || a.name}</strong>
                        {a.is_super === 1 && (
                          <span className="super-badge">SUPER</span>
                        )}
                      </div>
                    </td>
                    <td>{a.email}</td>
                    <td>{a.admin_phone || 'N/A'}</td>
                     <td><span className={`badge-role ${a.role}`}>{a.role}</span></td>
                    
                    {/* Show to Users Toggle */}
                    <td>
                      <div className="toggle-wrapper" title={currentRole === 'admin' ? 'Show/Hide admin in property details' : 'Only admin can update'}>
                        <label className="toggle-label">
                          <input 
                            type="checkbox" 
                            checked={a.show_user} 
                            disabled={currentRole !== 'admin'}
                            onChange={async (e) => {
                              const newValue = e.target.checked;
                              setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, show_user: newValue } : admin));
                              try {
                                const token = getAuthToken();
                                await axios.put(`${API_URL}/admin/admins/${a._id}`, { show_user: newValue }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Status updated');
                              } catch {
                                setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, show_user: !newValue } : admin));
                                toast.error('Failed to update status');
                              }
                            }} 
                          />
                          <div className="toggle-switch mini"></div>
                        </label>
                        <span className="toggle-status-text">{a.show_user ? 'Visible' : 'Hidden'}</span>
                      </div>
                    </td>
                    <td>
                      <div className="toggle-wrapper" title={isSuperAdmin ? 'Allow this admin to use User Chat' : 'Only a Super Admin can change User Chat access'}>
                        <label className="toggle-label">
                          <input
                            type="checkbox"
                            checked={a.user_chat_access !== false}
                            disabled={!isSuperAdmin}
                            onChange={async (e) => {
                              const newValue = e.target.checked;
                              setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, user_chat_access: newValue } : admin));
                              try {
                                const token = getAuthToken();
                                await axios.put(`${API_URL}/admin/admins/${a._id}`, { user_chat_access: newValue }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('User Chat access updated');
                              } catch {
                                setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, user_chat_access: !newValue } : admin));
                                toast.error('Only a Super Admin can change User Chat access');
                              }
                            }}
                          />
                          <div className="toggle-switch mini"></div>
                        </label>
                        <span className="toggle-status-text">{a.user_chat_access !== false ? 'On' : 'Off'}</span>
                      </div>
                    </td>

                    {/* Super Admin Toggle */}
                    <td>
                      <div className="toggle-wrapper" title={isSuperAdmin ? "Toggle super admin rights" : "Only Super Admins can change this"}>
                        <label className={`toggle-label ${!isSuperAdmin ? 'disabled' : ''}`}>
                          <input 
                            type="checkbox" 
                            checked={a.is_super === 1}
                            disabled={!isSuperAdmin}
                            onChange={async (e) => {
                              const newValue = e.target.checked ? 1 : 0;
                              setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, is_super: newValue } : admin));
                              try {
                                const token = getAuthToken();
                                await axios.put(`${API_URL}/admin/admins/${a._id}`, { is_super: newValue }, {
                                  headers: { Authorization: `Bearer ${token}` }
                                });
                                toast.success('Super admin status updated');
                              } catch {
                                setAdmins(prev => prev.map(admin => admin._id === a._id ? { ...admin, is_super: newValue === 1 ? 0 : 1 } : admin));
                                toast.error('Failed to update super admin status');
                              }
                            }} 
                          />
                          <div className="toggle-switch mini"></div>
                        </label>
                        <span className="toggle-status-text">{a.is_super === 1 ? 'Yes' : 'No'}</span>
                      </div>
                    </td>

                    <td style={{ fontSize: '0.8rem', color: 'var(--admin-text-muted)' }}>
                      {a.createdAt ? new Date(a.createdAt).toLocaleDateString() : 'N/A'}
                    </td>
                    <td>
                      <div className="table-actions">
                        <button className="btn-icon" title="View Details" onClick={() => setViewingAdmin(a)}><FiEye /></button>
                        {currentRole === 'admin' && (
                          <button className="btn-icon" title="Edit Admin" onClick={() => handleEditClick(a)}><FiEdit2 /></button>
                        )}
                        {isSuperAdmin && (
                          <button className="btn-icon text-danger" title="Delete Admin" onClick={() => handleDeleteAdmin(a._id)}><FiTrash2 /></button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
      
      {/* View Details Modal */}
      {viewingAdmin && (
        <div className="admin-modal-overlay animate-fade-in" style={{ zIndex: 9999 }} onClick={() => setViewingAdmin(null)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Administrator Details</h2>
              <button className="btn-icon" onClick={() => setViewingAdmin(null)}><FiX /></button>
            </div>
            <div className="modal-form">
              <div className="filter-box-grid" style={{ gap: '2rem' }}>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Full Name</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingAdmin.admin_name || viewingAdmin.name}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Email Address</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingAdmin.email}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Phone Number</label>
                  <p style={{ fontSize: '1rem', fontWeight: 600, marginTop: '0.25rem' }}>{viewingAdmin.admin_phone || 'N/A'}</p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Role</label>
                  <p style={{ marginTop: '0.25rem' }}><span className={`badge-role ${viewingAdmin.role}`}>{viewingAdmin.role}</span></p>
                </div>
                <div className="detail-item">
                  <label style={{ fontSize: '0.75rem', textTransform: 'uppercase', color: 'var(--admin-text-muted)', fontWeight: 700 }}>Status</label>
                  <div style={{ marginTop: '0.5rem', display: 'flex', gap: '1rem' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className={`dot ${viewingAdmin.show_user ? 'bg-success' : 'bg-muted'}`} style={{ width: '8px', height: '8px', borderRadius: '50%', background: viewingAdmin.show_user ? '#10b981' : '#cbd5e1' }}></span>
                      <span style={{ fontSize: '0.8125rem' }}>{viewingAdmin.show_user ? 'Visible to Users' : 'Hidden from Users'}</span>
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                      <span className={`dot ${viewingAdmin.is_super === 1 ? 'bg-primary' : 'bg-muted'}`} style={{ width: '8px', height: '8px', borderRadius: '50%', background: viewingAdmin.is_super === 1 ? '#3b82f6' : '#cbd5e1' }}></span>
                      <span style={{ fontSize: '0.8125rem' }}>{viewingAdmin.is_super === 1 ? 'Super Admin' : 'Regular Admin'}</span>
                    </div>
                  </div>
                </div>
              </div>
              <div style={{ marginTop: '2.5rem', display: 'flex', justifyContent: 'flex-end' }}>
                <button className="btn-secondary" onClick={() => setViewingAdmin(null)}>Close Details</button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminList;
